import Tool from './tool';
import logging from '../logging';
import { registerListener } from '../html/register-listener';

const log = logging('tools');

type DragConsumer = Tool & {
    dragenter?: () => void;
    dragover?: (event: PointerEvent, target: HTMLElement) => void;
    dragleave?: () => void;
}

const intersectsElt = (event: PointerEvent, elt: HTMLElement): boolean => {
    const rect = elt.getBoundingClientRect();
    if(event.clientX > rect.right) return false;
    if(event.clientY < rect.top) return false;
    if(event.clientY > rect.bottom) return false;
    if(event.clientX < rect.left) return false;
    
    return true;
};

/**
 * Wraps another tool and a pair of HTML elements: the draggable element and
 * the drop target. The underlying tool is activated when the drag starts,
 * completed when the target receives the drop event, and canceled if the drag
 * stops outside of the drop target. If the underlying tool defines dragenter,
 * dragover, or dragleave methods, then the equivalent drag events are
 * dispatched to it. This is a sort of inversion-of-control scheme where the
 * DragTool drives the action of another tool, but this could just as well be
 * accomplished via subclassing; the advantage here is "composition over
 * inheritance," so we can still subclass something else and also use the
 * DragTool.
 */
export default class DragTool extends Tool {

    private elt: HTMLElement;
    private target: HTMLElement;
    private tool: DragConsumer;
    private isOverTarget: boolean = false;

    constructor(element: HTMLElement, target: HTMLElement, tool: Tool) {
        super();
        log.debug('DragTool::constructor');
        this.elt = element;
        this.target = target;
        this.tool = tool;
    }

    enable() {
        log.debug('DragTool::enable');
        registerListener(this.elt, 'pointerdown', this.begin);
    }

    disable() {
        log.debug('DragTool::disable');
        this.elt.removeEventListener('pointerdown', this.begin);
    }

    begin() {
        log.debug('DragTool::begin', this);
        this.tool.begin();

        registerListener(window, 'pointermove', this.pointermove);
        registerListener(window, 'pointerup', this.end);
    }


    /**
     * Note that the event's preventDefault() method must be called in order
     * to generate a drop event. This gives the referenced tool discretion over
     * whether or not the mouse is in a valid drop zone. If the tool doesn't
     * implement dragover, then the entire target is assumed to be the drop
     * zone.
     */
    pointermove = (event: PointerEvent) => {
        log.debug('DragTool::dragover'); // this is too noisy usually
        if(this.tool.dragover) {
            if(intersectsElt(event, this.target)) {
                if(!this.isOverTarget) {
                    this.isOverTarget = true;
                    if(this.tool.dragenter) this.tool.dragenter();
                }
                this.tool.dragover(event, this.target);
            } else {
                if(this.isOverTarget) {
                    this.isOverTarget = false;
                    if(this.tool.dragleave) this.tool.dragleave();
                }
            }

        }
    }

    end() {
        log.debug('DragTool::end');
        // we only dispatch to the underlying tool when there's been a drag,
        // otherwise it is never activated or completed.
        try {
            this.tool.end();
        } finally {
            this.cancel();
        }
    }

    cancel() {
        log.debug('DragTool::cancel');

        removeEventListener('pointermove', this.pointermove);
        removeEventListener('pointerup', this.end);
    }

}
